import { useCallback, useMemo, useState } from 'react'

import {
  type FanResponseDto,
  OfChatNotificationsService,
  type OnlyFansList,
} from '@api'
import { useUnit } from 'effector-react'
import last from 'lodash/fp/last'
import { useInfiniteQuery } from 'react-query'

import { $creatorId } from '@/models/creators'
import {
  usersListOrderOptions,
  usersListSortOptions,
} from '@/modules/chatting/consts/usersList'
import { GetUsersListFilters } from '@/modules/chatting/types'
import { NewMessageFan } from '@/modules/chatting/ui/components/NewMessageUsersListItem/NewMessageUsersListItem'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

const FANS_PAGINATION_SIZE = 10

export const useMassMessageFans = ({
  defaultIncludedLists = [],
}: {
  defaultIncludedLists?: OnlyFansList['id'][]
}) => {
  const creatorId = useUnit($creatorId)

  const [query, setQuery] = useState('')
  const [sort, setSort] = useState(usersListSortOptions[0].value)
  const [order, setOrder] = useState(usersListOrderOptions[0].value)
  const [filters, setFilters] = useState<GetUsersListFilters>({})

  const [includedLists, setIncludedLists] =
    useState<OnlyFansList['id'][]>(defaultIncludedLists)
  const [excludedLists, setExcludedLists] = useState<OnlyFansList['id'][]>([])
  const [includedUsers, setIncludedUsers] = useState<FanResponseDto['id'][]>([])
  const [excludedUsers, setExcludedUsers] = useState<FanResponseDto['id'][]>([])

  const [selectedFans, setSelectedFans] = useState<FanResponseDto['id'][]>([])
  const [selectedFansCount, setSelectedFansCount] = useState<number>(0)

  const fansQuery = useInfiniteQuery(
    // excludedUsers and includedUsers shouldn't trigger request
    [
      'fan-chats',
      creatorId,
      filters,
      query,
      includedLists,
      excludedLists,
      order,
      sort,
    ],
    ({ pageParam = 0 }) =>
      OfChatNotificationsService.getUsers({
        creatorId: creatorId!,
        offset: FANS_PAGINATION_SIZE * pageParam,

        requestBody: {
          sort,
          order,
          ...(query // Skip filters to find specific users
            ? { query }
            : {
                ...filters,
                excluded_users: excludedUsers.map(String),
                included_users: includedUsers.map(String),
                list_id: includedLists,
                excluded_lists: excludedLists,
              }),
        },
      }),
    {
      getNextPageParam: (page, pages) =>
        page.hasMore ? pages.length : undefined,
      refetchOnMount: true,
      enabled: !!creatorId,
      cacheTime: 0,
      onSuccess: (data) => {
        if (query) {
          return
        }

        if (includedLists.length) {
          const allFans = data.pages
            .flatMap((page) => page.list)
            .filter(isTruthyAndNotEmpty)

          const selectedFans = allFans.reduce<number[]>((fanIds, fan) => {
            if (!excludedUsers.includes(fan.id)) {
              fanIds.push(fan.id)
            }

            return fanIds
          }, [])

          setSelectedFans(selectedFans)
          setSelectedFansCount(last(data.pages)?.total ?? 0)
        } else {
          setSelectedFans(includedUsers)
          setSelectedFansCount(includedUsers.length)
        }
      },
    },
  )

  const onFetchNext = useCallback(() => {
    const { isFetchingNextPage, hasNextPage, fetchNextPage } = fansQuery

    if (!hasNextPage || isFetchingNextPage) return

    fetchNextPage()
  }, [fansQuery])

  const onClear = useCallback(() => {
    setIncludedLists([])
    setExcludedLists([])
    setIncludedUsers([])
    setExcludedUsers([])
    setSelectedFans([])
  }, [])

  const onToggle = useCallback((fan: NewMessageFan) => {
    const removeId = (fanIds: number[]) => fanIds.filter((id) => id !== fan.id)
    const addId = (fanIds: number[]) => fanIds.concat(fan.id)

    if (fan.isSelected) {
      setSelectedFans(removeId)
      setSelectedFansCount((count) => count - 1)

      setIncludedUsers((includedUsers) => {
        const isIncluded = !!includedUsers.find((id) => id === fan.id)

        if (isIncluded) {
          return removeId(includedUsers)
        } else {
          setExcludedUsers(addId)

          return includedUsers
        }
      })
    } else {
      setSelectedFans(addId)
      setSelectedFansCount((count) => count + 1)

      setExcludedUsers((excludedUsers) => {
        const isExcluded = !!excludedUsers.find((id) => id === fan.id)

        if (isExcluded) {
          return removeId(excludedUsers)
        } else {
          setIncludedUsers(addId)

          return excludedUsers
        }
      })
    }
  }, [])

  const isLoading = fansQuery.isLoading && !fansQuery.isFetchingNextPage
  const isFetchingNextPage = fansQuery.isFetchingNextPage

  const fans = useMemo(
    () =>
      fansQuery.data?.pages
        .flatMap((page) => page.list)
        .filter(isTruthyAndNotEmpty),
    [fansQuery.data],
  )

  return {
    order,
    setOrder,
    sort,
    setSort,
    query,
    setQuery,
    filters,
    setFilters,

    fans,
    isLoading,
    isFetchingNextPage,
    onToggle,
    onClear,
    onFetchNext,
    includedLists,
    setIncludedLists,
    excludedLists,
    setExcludedLists,
    includedUsers,
    excludedUsers,
    selectedFans,
    selectedFansCount,
  }
}
